import { Component, inject } from '@angular/core'
import { ZardDialogRef, Z_MODAL_DATA } from '@/styles/components/dialog'
import { IConfirmationDialogData } from '../../models/dialog.models'
import { ICON_PATHS } from '../../../core/constants/assets.constants'
import { BUTTON_TEXT, DEFAULT_DIALOG_TEXTS } from '../../../core/constants/button-text.constants'
import {
    ZardFormFieldComponent,
    ZardFormLabelComponent,
    ZardFormControlComponent,
} from '../../../../styles/components/form/form.component'
import { ZardInputDirective } from '../../../../styles/components/input/input.directive'
import { FormsModule } from '@angular/forms'
import { NotesTextareaFieldComponent } from '../notes-textarea-field/notes-textarea-field.component'
import { SHARED_NOTES_TEXTAREA_MIN_LENGTH } from '../../constants/notes-textarea-field.constants'

@Component({
    selector: 'app-confirmation-dialog',
    templateUrl: './confirmation-dialog.component.html',
    styleUrls: ['./confirmation-dialog.component.scss'],
    imports: [
        ZardFormFieldComponent,
        ZardFormLabelComponent,
        ZardFormControlComponent,
        ZardInputDirective,
        FormsModule,
        NotesTextareaFieldComponent,
    ],
})
export class ConfirmationDialogComponent {
    dialogRef = inject<ZardDialogRef<ConfirmationDialogComponent>>(ZardDialogRef)
    data = inject<IConfirmationDialogData>(Z_MODAL_DATA)

    iconPaths = ICON_PATHS
    buttonText = BUTTON_TEXT
    defaultTexts = DEFAULT_DIALOG_TEXTS
    remarkText = ''
    remarkInteracted = false

    constructor() {
        const data = this.data

        this.remarkText = data.remarkDefault || ''
    }

    handleConfirmClick(): void {
        if (this.data.collectRemark) {
            this.dialogRef.close({ confirmed: true, remark: this.remarkText })
        } else {
            this.dialogRef.close(true)
        }
    }

    handleCancelClick(): void {
        if (this.data.collectRemark) {
            this.dialogRef.close({ confirmed: false })
        } else {
            this.dialogRef.close(false)
        }
    }

    handleRemarkInteraction(value?: string): void {
        if (value !== undefined) {
            this.remarkText = value
        }
        this.remarkInteracted = true
    }

    /** Min length for {@link NotesTextareaFieldComponent} when CSL-style remark is used. */
    notesRemarkMinLength(): number | null {
        if (!this.data.useNotesTextareaForRemark) {
            return null
        }
        return this.data.remarkMinLength ?? SHARED_NOTES_TEXTAREA_MIN_LENGTH
    }

    remarkNotesErrorMessage(): string | null {
        if (!this.data.useNotesTextareaForRemark) {
            return null
        }
        const min = this.notesRemarkMinLength()
        if (min == null || min <= 0) {
            return null
        }
        if (!this.remarkInteracted) {
            return null
        }
        const len = this.remarkText.trim().length
        if (this.data.remarkRequired) {
            if (len < min) {
                return `Please enter at least ${min} characters.`
            }
        } else if (len > 0 && len < min) {
            return `If you add a remark, it must be at least ${min} characters.`
        }
        return null
    }

    isConfirmDisabled(): boolean {
        if (!this.data.collectRemark) {
            return false
        }
        if (!this.data.useNotesTextareaForRemark) {
            return !!(this.data.remarkRequired && !this.remarkText.trim())
        }
        const min = this.notesRemarkMinLength() ?? SHARED_NOTES_TEXTAREA_MIN_LENGTH
        const len = this.remarkText.trim().length
        if (this.data.remarkRequired) {
            return len < min
        }
        return len > 0 && len < min
    }
}
